import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ContentModalWinner, ModalChart } from './winners';

import { formatDni } from '.';

import { Button, Divider, Dropdown, Modal, NavBar, Tabs } from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import useAuth from '@/hooks/useAuth';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import ROUTES from '@/router/routes';
import {
  getRanking,
  getRankingLocalidad,
  getRankingPronvicias,
  initProvinciaRanking,
} from '@/services/ranking';
import { getTshirtAndShieldByIds } from '@/services/utils';
import { RankingTO } from '@/types/ranking';
import { formatNumberToLocale } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

const DTitem = ({
  DT,
  index,
  setSelectedWinner,
  onOpenChart,
  onOpenWinnerTeam,
  dropOptionSelected,
  isGeneralTournament,
}: {
  DT: RankingTO;
  index: number;
  setSelectedWinner?: (DT: RankingTO) => void;
  onOpenChart: () => void;
  onOpenWinnerTeam: () => void;
  dropOptionSelected: number;
  isGeneralTournament?: boolean;
}) => {
  const { userInitialData } = useAuth();
  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.rankings });

  const handleShowModalWinner = async () => {
    if (!showAsWinner) return;
    const response = await getTshirtAndShieldByIds([parseInt(DT.idUsuarioDT)]);

    if (response.estado && response.camisetas) {
      setSelectedWinner?.({
        ...DT,
        camisetaSvg: response.camisetas[0].cami,
      });
      onOpenWinnerTeam();
    }
  };
  const showAsWinner = index === 0 && dropOptionSelected === 0;

  const canViewTeam =
    showAsWinner &&
    (!isGeneralTournament ||
      (isGeneralTournament && userInitialData?.todoPublicado));

  return (
    <>
      <div className="grid grid-cols-12 px-6 mt-6">
        <div className="col-span-2 flex justify-start">
          <div
            className={`p-3 w-10 h-10 rounded-full flex justify-center items-center ${
              showAsWinner ? 'bg-gdt-blue-header' : 'bg-gdt-blue-light2'
            }`}
          >
            <span
              className={`font-bold ${
                showAsWinner ? 'text-white' : 'text-gdt-blue'
              }`}
            >
              {index + 1}
            </span>
          </div>
        </div>
        <div className="flex flex-col col-span-7">
          <span className="font-bold">{DT.dt}</span>
          {canViewTeam ? (
            <Button
              className="text-left"
              type="button"
              variant="text"
              onClick={handleShowModalWinner}
            >
              {DT.equipo}
            </Button>
          ) : (
            <span className="font-semibold text-gdt-gray-4">{DT.equipo}</span>
          )}

          <span className="font-semibold text-gdt-gray-4">{DT.provincia}</span>
        </div>
        <div className="flex flex-col col-span-3 text-end">
          <span className="col-span-2 font-bold text-end">{DT.puntos}</span>
          {showAsWinner &&
            DT.idUsuarioDT !== (userInitialData?.idUsuarioDT ?? '') && (
              <Button
                className="font-bold text-sm text-end "
                href="#"
                id="my-anchor-element"
                type="link"
                variant="text"
                onClick={() => {
                  setSelectedWinner?.(DT);
                  onOpenChart();
                }}
              >
                Comparar
              </Button>
            )}

          <Tooltip
            anchorSelect="#my-anchor-element"
            content="Al hacer clic acá vas a poder ver información del equipo"
          />
        </div>
        {showAsWinner && (
          <>
            <Divider className="my-3 col-span-12" type="horizontal" />

            <div className="flex justify-between col-span-12 text-end">
              <div className="flex flex-col">
                <span className=" text-start">
                  {DT?.edad ? `${DT.edad} años` : ''}{' '}
                </span>
                <span>{formatDni(DT.dni)}</span>
              </div>
              <div className="flex flex-col">
                <span>
                  Ranking general: <strong>{DT.puestoGral}</strong>
                </span>
                <span>
                  Hincha de: <strong>{DT.hinchaDe}</strong>
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      <Divider className="my-3" type="horizontal" />
    </>
  );
};

const RankingMobile = () => {
  const navigate = useNavigate();
  const goToCompleteData = () => {
    navigate(ROUTES.editAccount.myData);
  };

  const [selectedWinner, setSelectedWinner] = useState<RankingTO | null>(null);
  const { userInitialData } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRanking, setSelectedRanking] = useState(0);
  const [isOpenChartModal, setIsOpenChartModal] = useState<0 | 1 | 2>(0);
  const [isOpenWinnerTeamModal, setIsOpenWinnerTeamModal] = useState(false);
  const [isNoRankingsModalOpen, setIsNoRankingsModalOpen] = useState(false);

  const [dropOptioSelected, setDropOptionSelected] = useState(0);
  const [data, setData] = useState<RankingTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestUpdateAddress, setIsRequestUpdateAddress] = useState(false);

  const [msjFechaFinal, setMsjFechaFinal] = useState<{
    f11: string;
    f5: string;
  }>({ f11: '', f5: '' });

  const shouldShowAlert =
    !userInitialData?.barrio && !userInitialData?.dtExtranjero;
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    setData([]);
    if (
      (!userInitialData?.barrio || !userInitialData?.provincia) &&
      dropOptioSelected >= 1 &&
      !userInitialData?.dtExtranjero
    ) {
      setIsRequestUpdateAddress(true);
    }
    if (selectedTab === 0) {
      setIsLoading(true);
      const fetchData = async () => {
        try {
          if (dropOptioSelected === 0) {
            if (userInitialData?.fechaMostrarRankingF11?.id === undefined) {
              setData([]);
              return;
            }
            const ranking = await getRanking(
              selectedTab === 0 && selectedRanking === 0 ? 'f11Fecha' : 'f11',
              userInitialData?.fechaMostrarRankingF11.id.toString() ?? '1111'
            ).catch(() => {
              setData([]);
              return null;
            });
            if (ranking && 'top_fecha' in ranking) {
              setData(ranking.top_fecha as RankingTO[]);
            } else if (ranking && 'top_general' in ranking) {
              setData(ranking?.top_general as RankingTO[]);
            } else {
              setData([]);
            }

            if (ranking?.msjFechaFinal) {
              setMsjFechaFinal((prev) => ({
                ...prev,
                f11: ranking.msjFechaFinal ?? '',
              }));
            }
          }

          if (dropOptioSelected === 1) {
            const pronviciaData = await initProvinciaRanking();
            if (
              !pronviciaData?.datos?.idProvincia ||
              !userInitialData?.provincia
            ) {
              setData([]);
              return;
            }
            const provinciasRanking = await getRankingPronvicias(
              pronviciaData.datos.idProvincia.toString(),
              selectedTab === 0 && selectedRanking === 0
            );
            setData(
              provinciasRanking.datos.datos.map((e) => ({
                objectId: Math.random().toString(),
                dt: e.dt,
                equipo: e.equipo,
                puntos: formatNumberToLocale(e.puntos),
                dni: e.idDt
                  .toString()
                  .replace(/(\d{2})(\d{3})(\d{3})/, 'XX.XXX.$3'),
                idUsuarioDT: Math.random().toString(),
                hinchaDe: '',
                puestoGral: e.posicion.toString(),
                provincia: pronviciaData.datos.nombreProvincia,
                edad: '',
                escudo: '',
                localidad: '',
              }))
            );
          }

          if (dropOptioSelected === 2) {
            if (!userInitialData?.barrio) {
              setData([]);
              return;
            }
            const localidadesRanking = await getRankingLocalidad(
              !userInitialData?.dtExtranjero
            );
            if (selectedRanking === 0) {
              setData(
                localidadesRanking.datos.datosGeneral.map((e) => ({
                  objectId: Math.random().toString(),
                  dt: e.dt,
                  equipo: e.equipo,
                  puntos: formatNumberToLocale(e.puntos),
                  dni: '',
                  idUsuarioDT: Math.random().toString(),
                  hinchaDe: '',
                  puestoGral: e.posicion.toString(),
                  provincia: '',
                  edad: '',
                  escudo: '',
                  localidad: '',
                }))
              );
            } else {
              setData(
                localidadesRanking.datos.datosFecha.map((e) => ({
                  objectId: Math.random().toString(),
                  dt: e.dt,
                  equipo: e.equipo,
                  puntos: formatNumberToLocale(e.puntos),
                  dni: '',
                  idUsuarioDT: Math.random().toString(),
                  hinchaDe: '',
                  puestoGral: e.posicion.toString(),
                  provincia: '',
                  edad: '',
                  escudo: '',
                  localidad: '',
                }))
              );
            }
          }
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [
    selectedTab,
    userInitialData?.fechaMostrarRankingF11?.id,
    dropOptioSelected,
    selectedRanking,
    userInitialData?.barrio,
    userInitialData?.provincia,
    userInitialData?.dtExtranjero,
  ]);

  useEffect(() => {
    setData([]);
    if (selectedTab === 1) {
      setIsLoading(true);
      const fetchData = async () => {
        try {
          const ranking = await getRanking(
            selectedTab === 1 && selectedRanking === 0 ? 'f5Fecha' : 'f5',
            userInitialData?.fechaMostrarRankingF5?.id.toString() ?? '1111'
          );

          setData(ranking?.top_fecha ?? ranking?.top_general ?? []);
          if (ranking?.msjFechaFinal) {
            setMsjFechaFinal((prev) => ({
              ...prev,
              f5: ranking.msjFechaFinal ?? '',
            }));
          }
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [
    selectedTab,
    userInitialData?.fechaMostrarRankingF5?.id,
    selectedRanking,
  ]);

  useEffect(() => {
    if (shouldShowAlert) {
      setShowAlert(true);
    }
  }, [shouldShowAlert]);

  const f11DropOptions = [
    { label: 'Ranking General', value: 0, disabled: false },
    ...(userInitialData?.dtExtranjero
      ? [
          {
            label: 'Ranking de tu país',
            value: 2,
            disabled: false,
          },
        ]
      : [
          {
            label: 'Ranking de tu provincia',
            value: 1,
            disabled: false,
          },
          {
            label: 'Ranking de tu localidad/barrio',
            value: 2,
            disabled: false,
          },
        ]),
  ];
  const f5DropOptions = [
    { label: 'Ranking General', value: 0, disabled: false },
  ];
  const DropOptions = selectedTab === 0 ? f11DropOptions : f5DropOptions;

  // Check for empty rankings and show modal
  useEffect(() => {
    const shouldShowModal =
      data?.length === 0 &&
      dropOptioSelected !== undefined &&
      userInitialData?.fecha_publicada === false; // No published date in tournament

    setIsNoRankingsModalOpen(shouldShowModal);
  }, [data, dropOptioSelected, userInitialData?.fecha_publicada]);

  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          title: 'Rankings',
        },
      }}
    >
      <div className="mx-6">
        <Tabs
          activeTab={selectedTab}
          className="mt-4 "
          tabs={[{ name: 'Primera División' }, { name: 'Fútbol 5' }]}
          variant="primary"
          onClick={(e) => setSelectedTab(e)}
        />
      </div>
      {/* banner horizontal */}
      <AdSlot
        className="mt-4"
        divId={ADS_DIV_ID.horizontal}
        variant="HORIZONTAL"
      />
      <div className="mx-6">
        <Tabs
          activeTab={selectedRanking}
          className="mt-4"
          tabs={[
            {
              name: `${
                selectedTab === 0
                  ? userInitialData?.fechaMostrarRankingF11?.nombre
                  : userInitialData?.fechaMostrarRankingF5?.nombre
              }`,
            },
            { name: 'Torneo General' },
          ]}
          variant="underline"
          onClick={(e) => setSelectedRanking(e)}
        />
      </div>
      <div className="mx-6 mt-6">
        <Dropdown
          className=""
          options={DropOptions}
          selected={
            DropOptions.find((e) => e.value === dropOptioSelected) ||
            DropOptions[0]
          }
          onChange={(e) => setDropOptionSelected(e.value)}
        />
      </div>
      {showAlert && (
        <div className="mx-6 p-3 bg-gdt-gray-2 rounded-lg relative mt-2">
          <p className="text-sm text-white max-w-[90%]">
            Para conocer el ranking de tu provincia o localidad, debés completar
            tus datos. <br />
            <button className="font-bold" onClick={goToCompleteData}>
              Completar
            </button>
          </p>
          <button
            className="absolute top-3 right-3 text-white hover:opacity-70"
            onClick={() => setShowAlert(false)}
          >
            <X className="absolute top-3 right-3 text-white" />
          </button>
        </div>
      )}
      <div className="mx-6 mt-6">
        <div className="grid grid-cols-12 ">
          <span className="col-span-2 font-semibold text-gdt-gray-2">Pos</span>
          <span className="col-span-8 font-semibold text-gdt-gray-2">DT</span>
          <span className="col-span-2 font-semibold text-gdt-gray-2 text-end">
            Puntos
          </span>
        </div>
      </div>
      {data?.slice(0, 10).map((DT, index) => (
        <DTitem
          key={DT.objectId}
          DT={DT}
          dropOptionSelected={dropOptioSelected}
          index={index}
          isGeneralTournament={selectedRanking === 1}
          setSelectedWinner={setSelectedWinner}
          onOpenChart={() => setIsOpenChartModal((selectedTab + 1) as 1 | 2)}
          onOpenWinnerTeam={() => setIsOpenWinnerTeamModal(true)}
        />
      ))}
      {!isLoading && data?.length === 0 && (
        <div className="text-center text-gdt-gray-2 mt-4">
          {selectedRanking === 1 &&
          (selectedTab === 0 ? msjFechaFinal.f11 : msjFechaFinal.f5)
            ? selectedTab === 0
              ? msjFechaFinal.f11
              : msjFechaFinal.f5
            : 'No existen rankings disponibles en este momento.'}
        </div>
      )}
      <Modal
        className="pt-7 px-10 bg-gdt-gray-7"
        controlledOnClose={() => {
          setIsOpenWinnerTeamModal(false);
          setSelectedWinner(null);
        }}
        isControlledOpen={isOpenWinnerTeamModal}
        render={() => {
          return (
            <ContentModalWinner
              DT={selectedWinner}
              isDate={selectedRanking === 0}
              isF5={selectedTab === 1}
            />
          );
        }}
        size="xs"
        title={
          selectedRanking === 0 ? 'Ganador de la fecha' : 'Ganador del torneo'
        }
        variant="controlled"
      />
      <Modal
        className="pt-7 px-10 bg-gdt-gray-7"
        controlledOnClose={() => {
          setIsOpenChartModal(0);
          setSelectedWinner(null);
        }}
        isControlledOpen={isOpenChartModal !== 0}
        render={() => {
          return (
            <ModalChart
              isF5={selectedTab === 1}
              isGeneralTournament={selectedRanking === 1}
              selectedWinner={selectedWinner}
            />
          );
        }}
        size="sm"
        title={isOpenChartModal === 1 ? 'Primera división' : 'Fútbol 5'}
        variant="controlled"
      />
      <Modal
        controlledOnClose={() => setIsRequestUpdateAddress(false)}
        isControlledOpen={isRequestUpdateAddress}
        render={() => (
          <>
            <p className="p-3 text-center">
              {dropOptioSelected === 2
                ? 'Para conocer el Ranking de tu Localidad o Barrio, previamente debés completar tus datos.'
                : 'Para conocer el Ranking de tu Provincia, previamente debés completar tus datos.'}{' '}
              <br />
            </p>
            <Button
              className="w-11/12 ml-4"
              type="button"
              variant="primary"
              onClick={goToCompleteData}
            >
              Completar
            </Button>
          </>
        )}
        variant="controlled"
      />
      <Modal
        className="pt-7 px-10 bg-gdt-gray-7"
        controlledOnClose={() => setIsNoRankingsModalOpen(false)}
        isControlledOpen={isNoRankingsModalOpen}
        render={() => {
          return (
            <div className="text-center py-8">
              <p className="text-lg text-gdt-gray-2">
                No existen rankings disponibles en este momento
              </p>
              <div className="mt-8">
                <Button
                  className="px-8 py-2"
                  type="button"
                  variant="primary"
                  onClick={() => setIsNoRankingsModalOpen(false)}
                >
                  Aceptar
                </Button>
              </div>
            </div>
          );
        }}
        size="xs"
        title="Rankings"
        titleClassName="text-xl"
        variant="controlled"
      />
      {/* box banners */}
      <div className="justify-center flex lg:space-x-[99px] lg:mb-12 lg:mt-14 my-6">
        <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
      </div>
    </NavBar>
  );
};

export default RankingMobile;
